"use client";

import { useMemo, useState, type ReactNode } from "react";
import { Section } from "../../primitives";
import { DrlpInfoModal } from "../info-modal";
import { type DrlpCta } from "../cta";
import DrlpSectionHeader from "../section-header";
import { CSAT_BENCHMARKS, CSAT_BENCHMARKS_AS_OF } from "./data";
import { scoreToPercentile } from "./percentile";
import { IndustrySelect } from "./industry-select";
import { ScoreInput } from "./score-input";
import { PercentileCurve } from "./percentile-curve";
import { ComparisonBars } from "./comparison-bars";

export interface DrlpCsatBenchmarkProps {
  title?: ReactNode;
  badge?: string;
  description?: string | string[];
  primaryCta?: DrlpCta;
  secondaryCta?: DrlpCta;
  align?: "vertical" | "horizontal";
  /** Tailwind fill utility for the percentile marker pill. Defaults to Fin orange. */
  markerFillClassName?: string;
  /** Tailwind stroke utility for the marker line at the user's score. Defaults to Fin orange. */
  markerStrokeClassName?: string;
  /** Tailwind background utility for the "your score" comparison bar. Defaults to Fin orange. */
  accentBgClassName?: string;
  openMetadata: { analyticsId: string };
  closeMetadata: { analyticsId: string };
  sectionMetadata: { analyticsId: string };
}

const DEFAULT_BADGE = "CSAT BENCHMARKING";
const DEFAULT_TITLE = "See how your CSAT score compares";

const COPY = {
  title: "Customer Satisfaction Score",
  scoreLabel: "CSAT score",
  tooltip:
    "Customer Satisfaction Score (CSAT) measures the percentage of support conversations rated positively by the end user. After a conversation is resolved, customers are asked to rate their experience; CSAT is calculated as the number of positive ratings divided by the total number of ratings received, expressed as a percentage from 0-100.",
};

export default function DrlpCsatBenchmark({
  title = DEFAULT_TITLE,
  badge = DEFAULT_BADGE,
  description,
  primaryCta,
  secondaryCta,
  align,
  markerFillClassName = "fill-fin",
  markerStrokeClassName = "stroke-fin",
  accentBgClassName = "bg-fin",
  openMetadata,
  closeMetadata,
  sectionMetadata,
}: DrlpCsatBenchmarkProps) {
  const [industrySlug, setIndustrySlug] = useState(CSAT_BENCHMARKS[0].slug);
  const [score, setScore] = useState<number | null>(null);
  const [hasEdited, setHasEdited] = useState(false);

  const industry = useMemo(
    () =>
      CSAT_BENCHMARKS.find((b) => b.slug === industrySlug) ??
      CSAT_BENCHMARKS[0],
    [industrySlug],
  );

  const anchors = industry.blended;
  const percentile =
    score !== null ? scoreToPercentile(score / 100, anchors) : null;
  const isPlaceholder = !hasEdited || score === null;
  const footerScope =
    industry.slug === "all-industries" ? "" : `${industry.industry} `;
  const footnote = `Benchmarks based on anonymized ${COPY.title} data across ${industry.accountCount} qualifying ${footerScope}Intercom accounts, as of ${new Date(
    `${CSAT_BENCHMARKS_AS_OF}T00:00:00`,
  ).toLocaleDateString("en-US", {
    month: "long",
    year: "numeric",
  })}. Industries with fewer than 30 accounts are omitted to protect anonymity.`;

  const titleText = typeof title === "string" ? title : "csat-benchmark";
  const regionId = badge || titleText;

  return (
    <Section
      id={regionId.toLowerCase().replaceAll(" ", "-")}
      data-analytics-region={regionId}
      data-analytics-id={sectionMetadata.analyticsId}
      data-testid="drlp-csat-benchmark"
      gridCols="10"
      className="py-16 md:py-20"
      dotDecorators
    >
      <div className="col-span-full grid grid-cols-subgrid gap-y-6 md:gap-y-10">
        <DrlpSectionHeader
          title={title}
          badge={badge}
          description={description}
          primaryCta={primaryCta}
          secondaryCta={secondaryCta}
          align={align}
        />

        <div className="border-current/10 col-span-full flex flex-col gap-5 border p-4 md:p-6">
          <div className="flex flex-col gap-4 min-[820px]:flex-row min-[820px]:items-start min-[820px]:justify-between">
            <div className="min-w-0 min-[820px]:flex-1">
              <div className="text-heading-5">
                {isPlaceholder ? "Enter your" : "Your"} {COPY.scoreLabel}
                {isPlaceholder ? "" : " is"}{" "}
                <ScoreInput
                  value={score}
                  onChange={(next) => {
                    setScore(next);
                    setHasEdited(true);
                  }}
                  label={`Your ${COPY.scoreLabel}`}
                  muted={isPlaceholder}
                />
                <span className="ml-2 inline-flex translate-y-[-2px]">
                  <DrlpInfoModal
                    title={COPY.title}
                    openMetadata={openMetadata}
                    closeMetadata={closeMetadata}
                  >
                    <div className="max-w-[440px]">
                      <p className="text-body">{COPY.tooltip}</p>
                      <p className="text-body text-current/60 mt-4">
                        {footnote}
                      </p>
                    </div>
                  </DrlpInfoModal>
                </span>
              </div>
              <p className="text-body-lg text-current/70 mt-1">
                {score !== null
                  ? `Higher than ${Math.round(percentile ?? 0)}% of ${industry.industry.toLowerCase()} companies.`
                  : `Enter your score to see how you compare to ${industry.industry.toLowerCase()} peers.`}
              </p>
            </div>

            <div className="shrink-0">
              <IndustrySelect
                value={industry.slug}
                onChange={setIndustrySlug}
                industries={CSAT_BENCHMARKS}
              />
            </div>
          </div>

          <PercentileCurve
            percentile={percentile}
            markerFillClassName={markerFillClassName}
            markerStrokeClassName={markerStrokeClassName}
          />

          {score !== null && (
            <ComparisonBars
              yourScore={score}
              yourLabel={`Your ${COPY.title}`}
              industryMedian={anchors.p50 * 100}
              industryName={industry.industry}
              primaryBarClassName={accentBgClassName}
            />
          )}
        </div>
      </div>
    </Section>
  );
}
